import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { App, Spin, Modal, Button, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useMutation } from 'react-query';
import { useStore } from '../../store/store';
import { getBoxById } from '../../store/lookups';
import { getUrlParams } from '../../utils/browser';
import OrgChartPanel from './OrgChartPanel';
import BoxActionsPanel from '../common/BoxActionsPanel';
import { claimBox, releaseBox } from '../../collab/useEditorCollab';
import {
  syncOrgChartFromSource,
  createBlankOrganizationalChart,
  resolveOrganizationalChart,
} from '../../requests/organizationalCharts';
import {
  ORG_CHART_MODES,
  isOrgChartEditableInProposal,
  orgChartMode,
  orgChartResolveTarget,
  shouldCreateBlankOrgChart,
  shouldResolveOrgChart,
} from '../../constants/orgChart';

// L'iframe pointe vers l'hôte Rails (REACT_APP_API_URL sans le suffixe /api).
const apiHost = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');

const selector = (boxId) => ({
  currentDocument,
  currentProposal,
  fromPdf,
  updateBox,
  boxLocks,
  user,
  selectedBoxId,
  orgChartModalBoxId,
  openOrgChartModal,
  closeOrgChartModal,
}) => ({
  currentDocument,
  currentProposal,
  fromPdf,
  updateBox,
  boxLocks,
  user,
  selectedBoxId,
  modalOpen: orgChartModalBoxId === boxId,
  openOrgChartModal,
  closeOrgChartModal,
});

const OrgChart = ({ box }) => {
  const { t } = useTranslation();
  // `App.useApp()` et non `Modal.confirm` : la version statique rend hors de
  // l'arbre React, donc hors du ConfigProvider, et garde le zIndexPopupBase par
  // défaut — la modale repasserait sous la barre flottante (cf. Layout.jsx).
  const { modal: modalApi } = App.useApp();
  const {
    currentDocument,
    currentProposal,
    fromPdf,
    updateBox,
    boxLocks,
    user,
    selectedBoxId,
    modalOpen,
    openOrgChartModal,
    closeOrgChartModal,
  } = useStore(selector(box.id));
  const isSelected = selectedBoxId === box.id;
  // Identifie l'iframe de CETTE box parmi tous les messages 'message' reçus (plusieurs
  // box org_chart peuvent être ouvertes simultanément dans le même proposal).
  const iframeRef = useRef(null);

  const isTemplate = currentDocument.type === 'template';
  const proposalId = currentProposal?.id;
  const { orgChartId, orgChartSourceId } = box;
  const mode = orgChartMode(box);
  // Organigramme du modèle : figé dans la proposition sauf déverrouillage — pas
  // de bouton d'édition, donc pas de copie créée pour rien.
  const editable = isTemplate || isOrgChartEditableInProposal(box);
  const imageUrl = box.content?.imageUrl;

  // orgChartSourceId = original chart (never changes after first select).
  // orgChartId       = copy tied to this document (updated after fork).
  const isCopy = orgChartSourceId && orgChartId !== orgChartSourceId;

  const jwt = getUrlParams('jwt') || window.localStorage.getItem('jwt');

  // Blank charts are already proposal-scoped — no fork needed (no proposal_id in URL).
  // Selected charts (orgChartSourceId set) need fork-on-open via proposal_id.
  const sourceChartId = orgChartSourceId || orgChartId;
  const needsFork = !!orgChartSourceId;
  const embedUrl = sourceChartId
    ? `${apiHost}/organizational_charts/${sourceChartId}/embed?jwt=${encodeURIComponent(jwt || '')}${needsFork && proposalId ? `&proposal_id=${proposalId}` : ''}`
    : null;

  // Verrou collab : un co-éditeur détient la box.
  const lockHolder = boxLocks[box.id];
  const lockedByOther = !!(lockHolder && lockHolder.id !== user?.id);

  // Si un co-éditeur prend le verrou pendant qu'on a la modal ouverte → fermer.
  useEffect(() => {
    if (modalOpen && lockedByOther) {
      closeOrgChartModal();
      releaseBox(box.id);
      message.warning(`${t('org_chart_locked_by')} ${lockHolder.name}`);
    }
  }, [lockedByOther, modalOpen, lockHolder?.name, box.id, t, closeOrgChartModal]);

  // Le composant est démonté à chaque changement de sélection (BoxWrapper échange
  // Dragger <-> Resizer) : on ne peut donc pas relâcher le verrou sur un simple
  // unmount. On le reprend au montage tant que la modal est ouverte, et on ne
  // relâche que si la box a réellement disparu du document.
  useEffect(() => {
    if (modalOpen) claimBox(box.id);
    return () => {
      const { orgChartModalBoxId, boxes, masks, closeOrgChartModal: close } = useStore.getState();
      if (orgChartModalBoxId !== box.id) return;
      if (getBoxById(boxes, masks, box.id)) return;
      releaseBox(box.id);
      close();
    };
  }, [box.id, modalOpen]);

  // Recalcule le ratio largeur/hauteur à chaque changement de PNG (sélection, création,
  // édition) — verrouille le resize proportionnel (comme une box photo).
  useEffect(() => {
    if (!imageUrl) return undefined;
    const img = new window.Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        updateBox(box.id, (b) => {
          b.content = { ...b.content, ratio: img.naturalWidth / img.naturalHeight };
        });
      }
    };
    img.src = imageUrl;
    return undefined;
  }, [imageUrl, box.id, updateBox]);

  const handleChangeChart = () => {
    releaseBox(box.id);
    closeOrgChartModal();
    updateBox(box.id, (b) => {
      b.orgChartId = null;
      b.orgChartSourceId = null;
      // Le commercial reprend la main : sans ça, une box en mode variable se
      // reposerait l'organigramme par défaut au rechargement suivant.
      b.orgChartMode = ORG_CHART_MODES.CHOOSE;
      b.content = { imageUrl: null, name: null, ratio: null };
    });
  };

  const openModal = () => {
    if (lockedByOther) {
      message.warning(`${t('org_chart_locked_by')} ${lockHolder.name}`);
      return;
    }
    claimBox(box.id);
    openOrgChartModal(box.id);
  };

  const closeModal = () => {
    releaseBox(box.id);
    closeOrgChartModal();
  };

  // Rafraîchit le PNG et met à jour orgChartId quand l'iframe signale une sauvegarde ou un fork.
  // Filtre par event.source : sans ça, chaque box org_chart du proposal reçoit les messages
  // de TOUTES les iframes ouvertes et écraserait son PNG avec celui d'une autre box.
  useEffect(() => {
    const onMessage = (event) => {
      if (apiHost && event.origin !== apiHost) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      if (event.data?.type === 'orgchart:saved' && event.data.imageUrl) {
        updateBox(box.id, (b) => {
          b.content = { ...b.content, imageUrl: event.data.imageUrl };
        });
      }

      if (event.data?.type === 'orgchart:forked' && event.data.orgChartId !== orgChartId) {
        updateBox(box.id, (b) => {
          b.orgChartId = event.data.orgChartId;
        });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [box.id, orgChartId, updateBox]);

  const { mutate: syncFromSource } = useMutation(
    () => syncOrgChartFromSource(orgChartId),
    {
      onSuccess: (chart) => {
        updateBox(box.id, (b) => {
          b.content = { ...b.content, imageUrl: chart.image_url };
        });
      },
      onError: () => message.error(t('org_chart_sync_error')),
    }
  );

  // Confirmation avant reset depuis la source (action destructive : perd les edits locaux).
  const confirmSync = () => {
    modalApi.confirm({
      title: t('org_chart_sync_confirm'),
      okText: t('org_chart_sync_button'),
      cancelText: t('cancel'),
      onOk: () => syncFromSource(),
    });
  };

  // Mode variable : le serveur choisit l'organigramme de l'agence visée. Sans
  // réponse (aucun défaut, plusieurs candidats), la box reste vide et le
  // sélecteur habituel prend le relais.
  const autoResolveFiredRef = useRef(false);
  const { mutate: resolveChart, isLoading: resolving } = useMutation(
    () => resolveOrganizationalChart(proposalId, mode, orgChartResolveTarget(box)),
    {
      onSuccess: ({ chart }) => {
        updateBox(box.id, (b) => {
          b.orgChartResolved = true;
          // Écrase ce que la box traînait : en mode variable, seul le résultat
          // de la résolution fait foi. Sans organigramme trouvé, la box repart
          // vide et le sélecteur prend le relais.
          b.orgChartId = chart?.id ?? null;
          b.orgChartSourceId = chart?.id ?? null;
          b.content = chart
            ? { imageUrl: chart.image_url || null, name: chart.name || null, ratio: null }
            : { imageUrl: null, name: null, ratio: null };
        });
      },
      onError: () => message.error(t('org_chart_resolve_error')),
    }
  );

  useEffect(() => {
    if (autoResolveFiredRef.current) return;
    if (!shouldResolveOrgChart({ box, isTemplate, proposalId })) return;
    autoResolveFiredRef.current = true;
    resolveChart();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const autoCreateFiredRef = useRef(false);
  const { mutate: createBlank, isLoading: creatingBlank } = useMutation(
    () => createBlankOrganizationalChart(proposalId),
    {
      onSuccess: (chart, { openAfter } = {}) => {
        updateBox(box.id, (b) => {
          b.orgChartResolved = true;
          b.orgChartId = chart.id;
          b.orgChartSourceId = null;
          b.content = { imageUrl: chart.image_url || null, name: chart.name || null, ratio: null };
        });
        if (openAfter) {
          claimBox(box.id);
          openOrgChartModal(box.id);
        }
      },
      onError: () => message.error(t('org_chart_create_error')),
    }
  );

  // Mode vierge : l'organigramme est créé à l'injection.
  // openAfter:false → affiche l'état PNG "cliquer pour éditer" sans ouvrir la modal.
  useEffect(() => {
    if (autoCreateFiredRef.current) return;
    if (!shouldCreateBlankOrgChart({ box, isTemplate, proposalId })) return;
    autoCreateFiredRef.current = true;
    createBlank();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const placeholder = (text, action = null) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        minHeight: 160,
        width: box.width,
        padding: '12px 8px',
        backgroundColor: '#fafafa',
        border: '1px dashed #d9d9d9',
        color: '#8c8c8c',
      }}
    >
      <FontAwesomeIcon icon={icon({ name: 'sitemap', style: 'light' })} style={{ fontSize: 28 }} />
      <span
        style={{
          maxWidth: 260,
          fontSize: 12,
          lineHeight: 1.4,
          textAlign: 'center',
          color: '#8c8c8c',
        }}
      >
        {text}
      </span>
      {action}
    </div>
  );

  // Bouton d'ouverture de l'éditeur. Le clic sur la box elle-même ne fait que la
  // sélectionner (resize, déplacement, panel de droite) : ouvrir la modale au
  // moindre clic rendait la box impossible à manipuler.
  const openEditorButton = (block = false) => (
    <Button
      size='small'
      type={block ? 'default' : 'primary'}
      disabled={lockedByOther}
      icon={<FontAwesomeIcon icon={icon({ name: 'pen', style: 'light' })} />}
      onClick={(event) => {
        event.stopPropagation();
        openModal();
      }}
    >
      {t('org_chart_open_editor_button')}
    </Button>
  );

  // La modale sert dans les deux branches : composer l'organigramme du modèle,
  // ou éditer celui de la proposition.
  const chartModal = (
    <Modal
      open={modalOpen}
      onCancel={closeModal}
      footer={null}
      width="90vw"
      style={{ top: 20 }}
      title={box.content?.name || t('org_chart_modal_title')}
      rootClassName="org-chart-modal-root"
      destroyOnClose
    >
      <iframe
        ref={iframeRef}
        title={box.content?.name || 'organizational-chart'}
        src={embedUrl}
        style={{ width: '100%', height: '80vh', border: 'none', display: 'block' }}
      />
    </Modal>
  );

  // Mode PDF : toujours le PNG (l'iframe/modal ne se rend pas dans un PDF).
  if (fromPdf) {
    return imageUrl ? (
      <img src={imageUrl} alt={box.content?.name || ''} style={{ width: box.width, height: box.height }} />
    ) : null;
  }

  // Template : état de la box sur le canvas, réglage dans l'inspecteur.
  if (isTemplate) {
    return (
      <>
      {/* Seul le mode « organigramme de ce modèle » a quelque chose à montrer :
          partout ailleurs le contenu n'existe qu'une fois le document injecté
          dans un proposal, la box n'affiche donc qu'un état. Le réglage et son
          explication vivent dans l'inspecteur, qui a la largeur pour les porter
          — la box, souvent étroite (partage de ligne), y écrasait le texte sur
          six lignes. */}
      {mode === ORG_CHART_MODES.TEMPLATE && imageUrl ? (
        <img
          src={imageUrl}
          alt={box.content?.name || ''}
          onDoubleClick={openModal}
          style={{ width: box.width, height: box.height, display: 'block' }}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            minHeight: 160,
            width: box.width,
            padding: '12px 8px',
            backgroundColor: '#fafafa',
            border: '1px dashed #d9d9d9',
            color: '#8c8c8c',
          }}
        >
          <FontAwesomeIcon icon={icon({ name: 'sitemap', style: 'light' })} style={{ fontSize: 28 }} />
          <span style={{ fontWeight: 500, color: '#595959' }}>{t('org_chart_template_placeholder')}</span>
          <span style={{ maxWidth: 260, fontSize: 12, lineHeight: 1.4, textAlign: 'center' }}>
            {t(`org_chart_mode_${mode}`)}
          </span>
        </div>
      )}
      {isSelected ? (
        <>
          <OrgChartPanel box={box} isTemplate onEdit={openModal} />
          <BoxActionsPanel />
        </>
      ) : null}
      {chartModal}
    </>
    );
  }

  // Création (mode vierge) ou résolution (mode variable) en cours à l'injection.
  if (creatingBlank || resolving) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 160, width: box.width }}>
        <Spin tip={creatingBlank ? t('org_chart_creating') : t('org_chart_resolving')} />
      </div>
    );
  }

  // Aucun organigramme choisi → le picker (Select + créer vierge) vit dans le panel
  // de droite (OrgChartPanel) : cette box peut être resizée très étroite (partage de
  // ligne, comme une img) et n'a pas la place de l'héberger.
  // Pas de stopPropagation ici : il faut que le clic sélectionne normalement la box
  // pour que la section du panel s'affiche.
  if (!orgChartId) {
    return (
      <>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            minHeight: 160,
            width: box.width,
            backgroundColor: '#fafafa',
            border: '1px dashed #d9d9d9',
            color: '#8c8c8c',
          }}
        >
          <FontAwesomeIcon icon={icon({ name: 'sitemap', style: 'light' })} style={{ fontSize: 28 }} />
          <span style={{ textAlign: 'center', padding: '0 8px' }}>
            {isSelected ? t('org_chart_select_in_panel') : t('org_chart_select_hint')}
          </span>
        </div>
        {isSelected ? (
          <>
            <OrgChartPanel box={box} />
            <BoxActionsPanel />
          </>
        ) : null}
      </>
    );
  }

  // Organigramme choisi → aperçu PNG resizable comme une photo. Le simple clic
  // sélectionne la box (resize / déplacement / panel), l'édition passe par un
  // bouton explicite ; le double-clic sur l'aperçu reste un raccourci, comme on
  // ouvre un fichier. Pas de stopPropagation sur mousedown (contrairement au
  // sélecteur/template) : la box doit rester déplaçable en drawMode.
  return (
    <div style={{ width: box.width, height: box.height, position: 'relative' }}>
      {imageUrl ? (
        <div style={{ position: 'relative', width: box.width, height: box.height }}>
          <img
            src={imageUrl}
            alt={box.content?.name || ''}
            onDoubleClick={editable ? openModal : undefined}
            style={{ width: box.width, height: box.height, display: 'block' }}
          />
          {isSelected && editable && (
            lockedByOther ? (
              <div
                style={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  padding: '4px 10px',
                  borderRadius: 4,
                  backgroundColor: 'rgba(0, 0, 0, 0.65)',
                  color: 'white',
                  fontSize: 12,
                  pointerEvents: 'none',
                }}
              >
                {`${t('org_chart_locked_by')} ${lockHolder.name}`}
              </div>
            ) : (
              <div style={{ position: 'absolute', bottom: 8, left: 8 }}>
                {openEditorButton()}
              </div>
            )
          )}
        </div>
      ) : (
        placeholder(
          lockedByOther ? `${t('org_chart_locked_by')} ${lockHolder.name}` : t('org_chart_empty_hint'),
          lockedByOther || !editable ? null : openEditorButton(true)
        )
      )}
      {isSelected ? (
        <>
          <OrgChartPanel
            box={box}
            hasChart
            frozen={!editable}
            isCopy={isCopy}
            lockedByOther={lockedByOther}
            lockHolder={lockHolder}
            onEdit={openModal}
            onChangeChart={handleChangeChart}
            onSync={confirmSync}
          />
          <BoxActionsPanel />
        </>
      ) : null}
      {chartModal}
    </div>
  );
};

export default OrgChart;
